import React, { useEffect, useState } from "react";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Responsive from "@amzn/meridian/responsive";
import { VerticalLine } from "../vertical-line/VerticalLine";
import { MailInfo } from "./MailInfo";
import { Card } from "./Card";
import { Space } from "../space/Space";

import { getTotalOf } from "./utils";
import { useAppSelector } from "../../app/store/hooks";
import { MailAlerts } from "./MailAlerts";
import { StatusEnum, useLazyGetOverdueMailsQuery } from "../../endpoints/getArtifacts";
import { useLazyGetAggregateMailCardInfoByUserQuery } from "../../endpoints/getArtifactsCount";
import { useNavigate } from "react-router-dom";

export const MailCards: React.FC = () => {
    const { alias } = useAppSelector(state => state.auth.user);
    const [totalOverdue, setTotalOverdue] = useState(0);
    const [totalUnread, setTotalUnread] = useState(0);
    const [totalOpen, setTotalOpen] = useState(0);
    const navigate = useNavigate();
    const [getAggregateDocsByUser, { data: aggregateByUserData }] =
        useLazyGetAggregateMailCardInfoByUserQuery();
    const [getFailedMails, { data: failedMails }] = useLazyGetOverdueMailsQuery();

    useEffect(() => {
        getAggregateDocsByUser({
            useCase: "GET_AGGREGATED_DOCUMENTS",
            businessUseCase: "SMART",
            queryParams: {
                assignedTo: alias,
            },
        });
        getFailedMails({
            useCase: "GET_DOCUMENTS",
            businessUseCase: "SMART",
            queryParams: {
                limitPerRequest: 1,
                requestNumber: 1,
                status: [StatusEnum.FAILED],
            },
        });
    }, []);

    useEffect(() => {
        if (aggregateByUserData) {
            setTotalUnread(getTotalOf(aggregateByUserData.artifactCountMap?.teamCount, "UNREAD"));
            setTotalOverdue(getTotalOf(aggregateByUserData.artifactCountMap?.teamCount, "OVERDUE"));
            setTotalOpen(getTotalOf(aggregateByUserData.artifactCountMap?.teamCount, "OPEN"));
        }
    }, [aggregateByUserData]);

    return (
        <>
            <Responsive
                query="max-width"
                props={{
                    mailCardWrap: {
                        default: "none",
                        "799px": "down",
                    },
                }}
            >
                {({ mailCardWrap }) => (
                    <>
                        <Row
                            width="100%"
                            widths={["fit", "fill", "fit", "fit", "fit", "fit", "fit"]}
                            wrap={mailCardWrap}
                        >
                            <Text type="h400">Sorted mails</Text>
                            <div />

                            {totalOverdue > 0 && (
                                <MailInfo
                                    badgeType={"error"}
                                    count={totalOverdue}
                                    text={"Overdue"}
                                />
                            )}
                            {totalUnread > 0 && <MailInfo count={totalUnread} text={"Unread"} />}
                            {totalOpen > 0 && (
                                <MailInfo badgeType={"neutral"} count={totalOpen} text={"Open"} />
                            )}
                            {mailCardWrap === "none" && <VerticalLine height={30} />}
                            <Button
                                size="small"
                                onClick={() => navigate("/add-mail")}
                                type="tertiary"
                            >
                                Add mail
                            </Button>
                        </Row>
                        <Space size={"xxs"} />
                        {failedMails?.data?.totalCount > 0 && (
                            <MailAlerts
                                title={`${failedMails?.data?.totalCount} mails failed to be sorted`}
                                description={`You have ${failedMails?.data?.totalCount} mails 
                                that cannot be sorted automatically. Manually sort them immediately 
                                in case the due date is near.`}
                                detailsLink={"unsorted-mails"}
                            />
                        )}
                        <Space size={"xxs"} />
                        <Row
                            width={"100%"}
                            wrap={mailCardWrap}
                            alignmentHorizontal="center"
                            alignmentVertical="top"
                        >
                            <Card
                                header={"Assigned to me"}
                                data={aggregateByUserData?.artifactCountMap?.assignedToCount}
                                type={"ASSIGNED_TO_ME"}
                            />
                            <Card
                                header={"Real property"}
                                data={
                                    aggregateByUserData?.artifactCountMap?.teamCount?.REAL_PROPERTY
                                }
                                type={"REAL"}
                            />
                            <Card
                                header={"Personal property"}
                                data={
                                    aggregateByUserData?.artifactCountMap?.teamCount
                                        ?.PERSONAL_PROPERTY
                                }
                                type={"PERSONAL"}
                            />
                        </Row>
                    </>
                )}
            </Responsive>
        </>
    );
};
